/**
 * WastewaterPeerComparisonChart
 *
 * Horizontal bar chart comparing this city's per-capita wastewater emissions
 * against similar-sized California cities.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import type { WastewaterPeerCity } from '../../services/wastewaterAnalyticsService';

Chart.register(...registerables);

interface WastewaterPeerComparisonChartProps {
  peers: WastewaterPeerCity[];
  thisCity: { location_name: string; wastewater_per_capita: number | null };
}

const WastewaterPeerComparisonChart: React.FC<WastewaterPeerComparisonChartProps> = ({
  peers,
  thisCity,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  const destroyChart = useCallback(() => {
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    if (canvasRef.current) {
      try { const e = Chart.getChart(canvasRef.current); if (e) e.destroy(); } catch {}
    }
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;
    destroyChart();

    const allCities: Array<{ name: string; value: number | null; isThis: boolean; tier: number | null }> = [
      { name: thisCity.location_name, value: thisCity.wastewater_per_capita, isThis: true, tier: null },
      ...peers.map((p) => ({
        name: p.location_name,
        value: p.wastewater_per_capita,
        isThis: false,
        tier: p.data_tier,
      })),
    ]
      .filter((c) => c.value != null && c.value > 0)
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

    if (allCities.length === 0) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: allCities.map((c) => c.name),
        datasets: [
          {
            label: 'Per-capita wastewater emissions (MTCO₂e)',
            data: allCities.map((c) => c.value),
            backgroundColor: allCities.map((c) =>
              c.isThis ? '#0ea5e9' : 'rgba(107,114,128,0.55)'
            ),
            borderColor: allCities.map((c) => (c.isThis ? '#0369a1' : 'rgba(107,114,128,0.3)')),
            borderWidth: 1.5,
            borderRadius: 4,
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: `Per-Capita Wastewater Emissions — Peer City Comparison (2020)`,
            font: { size: 13, weight: 'bold' },
            color: '#1f2937',
            padding: { bottom: 10 },
          },
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const v = ctx.raw as number;
                const city = allCities[ctx.dataIndex];
                const tierLabel = city?.tier != null ? ` (Tier ${city.tier})` : '';
                return `${v.toLocaleString('en-US', { maximumFractionDigits: 5 })} MTCO₂e / person${tierLabel}`;
              },
              afterLabel: (ctx) => {
                const city = allCities[ctx.dataIndex];
                if (!city?.tier) return '';
                const tierDescriptions: Record<number, string> = {
                  1: 'Direct metered flow — very high confidence',
                  2: 'Measured facility volumes — high confidence',
                  3: 'Estimated facility volumes — moderate confidence',
                  4: 'Supplier-matched, facility inferred — moderate confidence',
                  5: 'Statewide per-capita default — preliminary estimate',
                };
                return tierDescriptions[city.tier] ?? '';
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: 'rgba(0,0,0,0.06)' },
            ticks: {
              color: '#6b7280',
              font: { size: 10 },
              callback: (v) =>
                Number(v).toLocaleString('en-US', { maximumFractionDigits: 5 }),
            },
            title: {
              display: true,
              text: 'MTCO₂e per capita',
              color: '#6b7280',
              font: { size: 11 },
            },
          },
          y: {
            grid: { display: false },
            ticks: { color: '#374151', font: { size: 11 } },
          },
        },
      },
    });

    return () => destroyChart();
  }, [peers, thisCity, destroyChart]);

  if (peers.length === 0 || thisCity.wastewater_per_capita == null) {
    return (
      <div style={{ padding: 32, color: '#6b7280', textAlign: 'center' }}>
        Peer comparison not available for this jurisdiction.
      </div>
    );
  }

  // Detect the two-cluster phenomenon (most cities use the same ICLEI 100 gpd/person factor)
  const allValues = peers.map(p => p.wastewater_per_capita ?? 0).filter(v => v > 0);
  const uniqueRounded = new Set(allValues.map(v => v.toFixed(4)));
  const clusterNote = uniqueRounded.size <= 2 && allValues.length >= 3
    ? 'Cities clustering at the same rate share the same per-capita treatment volume (~100 gpd/person) and emission factor. Cities below that cluster have lower measured treatment volumes.'
    : null;

  const chartHeight = Math.max(200, (peers.length + 1) * 36 + 80);

  return (
    <div style={{ position: 'relative', width: '100%', height: chartHeight }}>
      <canvas ref={canvasRef} />
      <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, textAlign: 'center' }}>
        Highlighted bar = {thisCity.location_name}. Peers selected by similar population (2020 data tier ≤ 5). Hover for data tier.
      </p>
      {clusterNote && (
        <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, textAlign: 'center' }}>
          {clusterNote}
        </p>
      )}
    </div>
  );
};

export default WastewaterPeerComparisonChart;
